import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, MessageCircle, X } from 'lucide-react';
import { Button } from './ui/button';

export default function MatchPopup({ user, matchId, onClose }) {
  const navigate = useNavigate();

  const photo = user.photos?.[0] || 'https://images.unsplash.com/photo-1694877164163-bd51561f794a?w=300';

  const handleChat = () => {
    onClose();
    navigate(`/app/chat/${matchId}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6"
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        className="relative w-full max-w-sm"
      >
        {/* Close button */}
        <button
          data-testid="close-match-popup"
          onClick={onClose}
          className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Card */}
        <div className="bg-zinc-900 rounded-3xl overflow-hidden border border-zinc-800 shadow-2xl">
          {/* Header */}
          <div className="p-6 text-center bg-gradient-to-b from-rose-600/20 to-transparent">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Heart className="w-8 h-8 text-rose-500 fill-rose-500" />
              <Heart className="w-6 h-6 text-rose-400 fill-rose-400" />
            </div>
            <h2 className="font-outfit text-2xl font-semibold text-white">
              It's a Match!
            </h2>
            <p className="text-zinc-400 text-sm mt-1">
              You and {user.name} liked each other
            </p>
          </div>

          {/* Photo */}
          <div className="px-6 -mt-2">
            <div className="relative">
              <img
                src={photo}
                alt={user.name}
                className="w-full aspect-square rounded-2xl object-cover match-glow"
              />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="bg-black/60 backdrop-blur-md rounded-xl px-4 py-2">
                  <h3 className="text-white font-semibold">
                    {user.name}, {user.age}
                  </h3>
                  {user.distance && (
                    <p className="text-zinc-400 text-sm">{user.distance} miles away</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="p-6 space-y-3">
            <Button
              data-testid="send-message-btn"
              onClick={handleChat}
              className="w-full h-12 bg-rose-600 hover:bg-rose-500 text-white font-semibold rounded-full"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Send a Message
            </Button>
            <Button
              data-testid="keep-swiping-btn"
              onClick={onClose}
              variant="outline"
              className="w-full h-12 border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-full"
            >
              Keep Swiping
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
